/**
 * Helper types and utilities for migrating from old error system to @repo/errors
 * This file provides compatibility layer while we migrate
 */

import { AppError as RepoAppError, AppError, errorHandler } from '@repo/errors'

// Extended error type used in therapist app
export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical'
export type ErrorCategory = 'auth' | 'network' | 'validation' | 'system'

export interface TherapistAppError extends RepoAppError {
  severity?: ErrorSeverity
  category?: ErrorCategory
  details?: string
  action?: {
    label: string
    href?: string
    onClick?: () => void
  }
}

// Error messages mapping used in therapist app
export const ERROR_MESSAGES = {
  // Authentication errors
  'auth/expired-link': {
    message: 'This password reset link has expired',
    severity: 'warning' as ErrorSeverity,
    category: 'auth' as ErrorCategory,
    details: 'Password reset links are valid for 1 hour for security reasons.',
    action: {
      label: 'Request new link',
      href: '/forgot-password'
    }
  },
  'auth/invalid-link': {
    message: 'Invalid password reset link',
    severity: 'error' as ErrorSeverity,
    category: 'auth' as ErrorCategory,
    details: 'This link appears to be invalid or has already been used.',
    action: {
      label: 'Request new link',
      href: '/forgot-password'
    }
  },
  'auth/session-expired': {
    message: 'Your session has expired',
    severity: 'warning' as ErrorSeverity,
    category: 'auth' as ErrorCategory,
    details: 'For security reasons, please request a new password reset.',
    action: {
      label: 'Start over',
      href: '/forgot-password'
    }
  },
  // Network errors
  'network/connection-lost': {
    message: 'Connection lost',
    severity: 'warning' as ErrorSeverity,
    category: 'network' as ErrorCategory,
    details: 'Please check your internet connection and try again.'
  },
  'network/offline': {
    message: 'You are offline',
    severity: 'warning' as ErrorSeverity,
    category: 'network' as ErrorCategory,
    details: 'This action requires an internet connection.'
  },
  // Validation errors
  'validation/password-mismatch': {
    message: 'Passwords do not match',
    severity: 'error' as ErrorSeverity,
    category: 'validation' as ErrorCategory,
    details: 'Please ensure both passwords are identical.'
  },
  'validation/password-weak': {
    message: 'Password is too weak',
    severity: 'error' as ErrorSeverity,
    category: 'validation' as ErrorCategory,
    details: 'Password must be at least 8 characters long and include uppercase, lowercase, and numbers.'
  },
  // System errors
  'system/unknown': {
    message: 'An unexpected error occurred',
    severity: 'error' as ErrorSeverity,
    category: 'system' as ErrorCategory,
    details: 'Please try again. If the problem persists, contact support.'
  },
  'system/unexpected': {
    message: 'An unexpected error occurred',
    severity: 'critical' as ErrorSeverity,
    category: 'system' as ErrorCategory,
    details: 'Something went wrong. Please try again later.'
  },
  // Additional validation errors
  'validation/required-fields': {
    message: 'Please fill in all required fields',
    severity: 'error' as ErrorSeverity,
    category: 'validation' as ErrorCategory
  }
} as const

/**
 * Convert @repo/errors AppError to TherapistAppError with extended properties
 */
export function enrichError(
  error: RepoAppError, 
  overrides?: Partial<TherapistAppError>
): TherapistAppError {
  const enriched = error as TherapistAppError
  
  // Set default severity based on status code
  if (!enriched.severity) {
    if (error.statusCode >= 500) {
      enriched.severity = 'critical'
    } else if (error.statusCode >= 400) {
      enriched.severity = 'error'
    } else {
      enriched.severity = 'warning'
    }
  }
  
  // Set category based on error code
  if (!enriched.category) {
    if (error.code.includes('AUTH')) {
      enriched.category = 'auth'
    } else if (error.code.includes('NETWORK')) {
      enriched.category = 'network'
    } else if (error.code.includes('VALIDATION')) {
      enriched.category = 'validation'
    } else {
      enriched.category = 'system'
    }
  }
  
  // Apply overrides
  if (overrides) {
    Object.assign(enriched, overrides)
  }
  
  return enriched
}

/**
 * Helper to check if user is online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine
}

/**
 * Retry with backoff (wrapper around @repo/errors withRetry)
 */
export { withRetry as retryWithBackoff } from '@repo/errors'

/**
 * Re-export parseError for convenience
 */
export { parseError } from '@repo/errors'

/**
 * Create a TherapistAppError from ERROR_MESSAGES entry
 */
export function createErrorFromMessage(
  messageKey: keyof typeof ERROR_MESSAGES,
  overrides?: Partial<TherapistAppError>
): TherapistAppError {
  const errorConfig = ERROR_MESSAGES[messageKey]
  const error = new AppError(errorConfig.message, {
    code: messageKey.toUpperCase().replace('/', '_'),
    statusCode: errorConfig.severity === 'critical' ? 500 : 400
  }) as TherapistAppError
  
  return Object.assign(error, errorConfig, overrides)
}